import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { authConfig, ClientPrincipal, getUserInfo } from "../authConfig";
import { bookingService } from "../services/bookingService";
import { BookingFormData, GiftCard } from "../types/Booking";
import { Evento } from "../types/Event";

interface BookingModalProps {
  evento: Evento;
  isWaitingList: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // Edit mode props
  editMode?: boolean;
  editId?: number;
  editType?: 'prenotazione' | 'listaAttesa';
  initialData?: BookingFormData;
  waitingListPosition?: number;
  // Gift card data for edit mode (already linked to reservation)
  initialGiftCard?: {
    Codice: string;
    NumeroAlcolico: number;
    NumeroAnalcolico: number;
  };
  // Whether the current viewer is the logged-in admin (always sees the exact date)
  isAdminView?: boolean;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6;

// Google icon component
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const initialFormData: BookingFormData = {
  Nome: "",
  Cognome: "",
  Email: "",
  Telefono: "",
  TipoSoggetto: "Privato",
  CodiceFiscale: "",
  PartitaIVA: "",
  RagioneSociale: "",
  Indirizzo: "",
  CAP: "",
  Citta: "",
  Provincia: "",
  Nazione: "Italia",
  NumeroPersone: 1,
  NumeroAlcolico: 0,
  NumeroAnalcolico: 1,
  NomiPartecipanti: [],
  Note: "",
};

const BookingModal: React.FC<BookingModalProps> = ({
  evento,
  isWaitingList,
  onClose,
  onSuccess,
  editMode = false,
  editId,
  editType,
  initialData,
  waitingListPosition,
  initialGiftCard,
  isAdminView = false,
}) => {
  const [step, setStep] = useState<Step>(editMode ? 2 : 1);
  const [formData, setFormData] = useState<BookingFormData>(initialData || initialFormData);
  const [allergyConfirmed, setAllergyConfirmed] = useState(editMode);
  const [termsAccepted, setTermsAccepted] = useState(editMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStep2Errors, setShowStep2Errors] = useState(false);
  const [showStep3Errors, setShowStep3Errors] = useState(false);
  const [showStep4Errors, setShowStep4Errors] = useState(false);
  const [useWaitingList, setUseWaitingList] = useState(isWaitingList);
  const [sendEmail, setSendEmail] = useState(true);
  // Admin editing a reservation or waiting list entry: no validation restrictions
  const isAdminEdit = editMode && isAdminView;
  
  // Auth state
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Gift card state
  const [giftCardCode, setGiftCardCode] = useState(initialData?.CodiceGiftCard || "");
  const [giftCardVerifying, setGiftCardVerifying] = useState(false);
  const [giftCardVerified, setGiftCardVerified] = useState<GiftCard | null>(null);
  const [giftCardError, setGiftCardError] = useState<string | null>(null);

  // Quiz game state
  interface QuizQuestion {
    question: string;
    answers: { text: string; correct: boolean }[];
  }
  const [quizActive, setQuizActive] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [quizCurrentIndex, setQuizCurrentIndex] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [quizSelectedAnswer, setQuizSelectedAnswer] = useState<number | null>(null);
  const [quizFinished, setQuizFinished] = useState(false);
  const [quizTimeLeft, setQuizTimeLeft] = useState(10);
  const [quizTimedOut, setQuizTimedOut] = useState(false);

  // Check if user is logged in on mount (skip in edit mode)
  useEffect(() => {
    // In edit mode, skip auth check - user data is already populated
    if (editMode) {
      setAuthLoading(false);
      return;
    }
    
    const checkAuth = async () => {
      try {
        const userInfo = await getUserInfo();
        setUser(userInfo);
        
        if (userInfo) {
          // User is logged in, pre-fill email from Google
          const email = userInfo.userDetails; // userDetails contains the email for Google auth
          
          // Try to get name from claims if available
          let firstName = "";
          let lastName = "";
          if (userInfo.claims) {
            const nameClaim = userInfo.claims.find(c => c.typ === "name");
            const givenNameClaim = userInfo.claims.find(c => c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname");
            const surnameCalim = userInfo.claims.find(c => c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname");
            
            if (givenNameClaim) firstName = givenNameClaim.val;
            if (surnameCalim) lastName = surnameCalim.val;
            
            // If we only have full name, try to split it
            if (!firstName && !lastName && nameClaim) {
              const nameParts = nameClaim.val.split(" ");
              firstName = nameParts[0] || "";
              lastName = nameParts.slice(1).join(" ") || "";
            }
          }
          
          setFormData(prev => ({
            ...prev,
            Email: email || prev.Email,
            Nome: firstName || prev.Nome,
            Cognome: lastName || prev.Cognome,
          }));
          
          // Skip login step if user is already logged in
          setStep(2);
        }
      } catch (err) {
        console.error("Error checking auth:", err);
      } finally {
        setAuthLoading(false);
      }
    };
    
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set initial gift card data in edit mode if there's a pre-existing gift card
  useEffect(() => {
    if (editMode && initialGiftCard) {
      // Use the gift card data passed from the reservation (already linked)
      setGiftCardVerified({
        Codice: initialGiftCard.Codice,
        NumeroAlcolico: initialGiftCard.NumeroAlcolico,
        NumeroAnalcolico: initialGiftCard.NumeroAnalcolico,
      } as GiftCard);
    }
  }, [editMode, initialGiftCard]);

  const handleGoogleLogin = () => {
    // Store booking intent in sessionStorage before redirecting
    sessionStorage.setItem("bookingIntent", JSON.stringify({
      eventoId: evento.EventoId,
      isWaitingList: isWaitingList,
    }));
    
    // Redirect to Google login
    const currentUrl = window.location.href;
    window.location.href = `${authConfig.loginUrl}?post_login_redirect_uri=${encodeURIComponent(currentUrl)}`;
  };

  const handleContinueAsGuest = () => {
    setStep(2);
  };

  // In edit mode the booking's own seats are already counted in PostiOccupati
  const postiGiaPrenotati = editMode && editType === "prenotazione" ? (initialData?.NumeroPersone || 0) : 0;
  const postiDisponibili = evento.PostiTotali - evento.PostiOccupati + postiGiaPrenotati;
  const exceedsCapacity = !isWaitingList && formData.NumeroPersone > postiDisponibili && postiDisponibili > 0;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (value === "" ? 0 : parseInt(value)) : value,
    }));
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === "" ? 0 : parseInt(value);
    setFormData((prev) => ({
      ...prev,
      [name]: numValue,
    }));
  };

  const handleNumeroPersoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numPersone = value === "" ? 0 : parseInt(value);
    // Initialize names array for additional participants (numPersone - 1, excluding the booker)
    const additionalCount = Math.max(0, numPersone - 1);
    const newNomi = Array(additionalCount).fill("").map((_, i) => 
      formData.NomiPartecipanti[i] || ""
    );
    setFormData((prev) => ({
      ...prev,
      NumeroPersone: numPersone,
      NumeroAnalcolico: numPersone,
      NumeroAlcolico: 0,
      NomiPartecipanti: newNomi,
    }));
  };

  const handleParticipantNameChange = (index: number, value: string) => {
    setFormData((prev) => {
      const newNomi = [...prev.NomiPartecipanti];
      newNomi[index] = value;
      return { ...prev, NomiPartecipanti: newNomi };
    });
  };

  // Step 3 validation: conditions accepted and allergy confirmed
  const isStep3Valid = () => termsAccepted && allergyConfirmed;

  // Step 4 validation: billing/contact data
  const isStep4Valid = () => {
    if (!formData.Nome.trim() || !formData.Cognome.trim()) return false;
    if (isAdminEdit) return true;
    if (!formData.Email.trim() || !formData.Telefono.trim()) return false;
    if (formData.TipoSoggetto === "Azienda" && !formData.PartitaIVA) return false;
    if (formData.TipoSoggetto === "Privato" && !formData.CodiceFiscale) return false;
    // Address fields are mandatory
    if (!formData.Indirizzo.trim() || !formData.CAP.trim() || !formData.Citta.trim() || !formData.Provincia.trim()) return false;
    return true;
  };

  // Gift card verification
  const handleVerifyGiftCard = async () => {
    if (!giftCardCode.trim()) return;
    
    setGiftCardVerifying(true);
    setGiftCardError(null);
    setGiftCardVerified(null);
    
    try {
      const giftCard = await bookingService.verifyGiftCard(giftCardCode.trim());
      if (giftCard) {
        setGiftCardVerified(giftCard);
        setFormData(prev => ({
          ...prev,
          CodiceGiftCard: giftCard.Codice
        }));
      } else {
        setGiftCardError("Codice gift card non valido");
      }
    } catch (err: any) {
      setGiftCardError(err.message || "Errore nella verifica della gift card");
    } finally {
      setGiftCardVerifying(false);
    }
  };

  const handleClearGiftCard = () => {
    setGiftCardCode("");
    setGiftCardVerified(null);
    setGiftCardError(null);
    setFormData(prev => ({
      ...prev,
      CodiceGiftCard: undefined
    }));
  };

  const goToStep = (newStep: Step) => {
    setError(null);
    setShowStep2Errors(false);
    setShowStep3Errors(false);
    setShowStep4Errors(false);
    if (newStep === 3 && !isStep2Valid()) {
      setError("La somma delle bevande deve essere uguale al numero di partecipanti. Inserisci anche i nomi di tutti i partecipanti.");
      return;
    }
    if (newStep === 4 && !isStep3Valid()) {
      setError("Devi accettare le condizioni e confermare di aver preso visione degli ingredienti");
      return;
    }
    if (newStep === 5 && !isStep4Valid()) {
      setError("Compila tutti i campi obbligatori");
      return;
    }
    setStep(newStep);
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      setSaving(true);
      const dataToSend = {
        ...formData,
        CodiceGiftCard: giftCardVerified?.Codice || undefined,
      };
      
      if (editMode && editId) {
        // Edit mode - update existing record
        if (editType === 'listaAttesa') {
          if (isAdminEdit) {
            await bookingService.adminUpdateListaAttesa(editId, dataToSend);
          } else {
            await bookingService.updateListaAttesa(editId, dataToSend);
          }
        } else {
          if (isAdminEdit) {
            await bookingService.adminUpdatePrenotazione(editId, dataToSend, sendEmail && !!formData.Email.trim());
          } else {
            await bookingService.updatePrenotazione(editId, dataToSend);
          }
        }
      } else {
        // Create mode
        if (isWaitingList || useWaitingList) {
          await bookingService.addToWaitingList(evento.EventoId!, dataToSend);
        } else {
          await bookingService.createPrenotazione(evento.EventoId!, dataToSend);
        }
      }
      setStep(6);
    } catch (err: any) {
      setError(err.message || (editMode ? "Errore durante la modifica" : "Errore durante la prenotazione"));
    } finally {
      setSaving(false);
    }
  };

  const handlePlayGame = async () => {
    setQuizLoading(true);
    setQuizActive(true);
    
    try {
      const response = await fetch("/api/quiz/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ titolo: evento.Titolo }),
      });
      
      if (!response.ok) {
        throw new Error("Errore nella generazione del quiz");
      }
      
      const questions = await response.json();
      
      setQuizQuestions(questions);
      setQuizCurrentIndex(0);
      setQuizScore(0);
      setQuizAnswered(false);
      setQuizSelectedAnswer(null);
      setQuizFinished(false);
      setQuizTimeLeft(10);
      setQuizTimedOut(false);
    } catch (err) {
      console.error("Quiz error:", err);
      setQuizActive(false);
    } finally {
      setQuizLoading(false);
    }
  };

  const handleQuizAnswer = (answerIndex: number) => {
    if (quizAnswered) return;
    
    setQuizSelectedAnswer(answerIndex);
    setQuizAnswered(true);
    setQuizTimedOut(false);
    
    if (quizQuestions[quizCurrentIndex].answers[answerIndex].correct) {
      setQuizScore(prev => prev + 1);
    }
  };

  // Timer countdown effect
  useEffect(() => {
    if (!quizActive || quizAnswered || quizFinished || quizLoading || quizQuestions.length === 0) {
      return;
    }

    if (quizTimeLeft <= 0) {
      // Time's up - mark as answered without points
      setQuizAnswered(true);
      setQuizTimedOut(true);
      return;
    }

    const timer = setTimeout(() => {
      setQuizTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [quizActive, quizAnswered, quizFinished, quizLoading, quizQuestions.length, quizTimeLeft]);

  const handleNextQuestion = () => {
    if (quizCurrentIndex < quizQuestions.length - 1) {
      setQuizCurrentIndex(prev => prev + 1);
      setQuizAnswered(false);
      setQuizSelectedAnswer(null);
      setQuizTimeLeft(10);
      setQuizTimedOut(false);
    } else {
      setQuizFinished(true);
    }
  };

  const handleCloseQuiz = () => {
    setQuizActive(false);
    setQuizQuestions([]);
    setQuizCurrentIndex(0);
    setQuizScore(0);
    setQuizAnswered(false);
    setQuizSelectedAnswer(null);
    setQuizFinished(false);
    setQuizTimeLeft(10);
    setQuizTimedOut(false);
  };

  const handleCloseConfirmation = () => {
    onSuccess();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMonthYear = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      month: 'long',
      year: 'numeric',
    });
  };

  // In edit mode the customer already booked and knows the date; otherwise hide the exact
  // day from public visitors when the event is flagged with MostraGiornoEsatto = false.
  const displayDate = (editMode || isAdminView || evento.MostraGiornoEsatto !== false)
    ? formatDate(evento.DataEvento)
    : formatMonthYear(evento.DataEvento);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const calculateTotal = () => {
    return formData.NumeroAlcolico * (evento.PrezzoAlcolico || 0) +
           formData.NumeroAnalcolico * (evento.PrezzoAnalcolico || 0);
  };

  const StepIndicator = () => {
    // Don't show step indicator on step 1 (login choice) or step 6 (confirmation)
    if (step === 1 || step === 6) return null;
    
    const stepLabels = ["PERSONE", "CONDIZIONI", "DATI", "RIEPILOGO"];
    
    return (
      <Flex justify="center" align="center" mb={6} flexShrink={0} px={4}>
        {[2, 3, 4, 5].map((s, i) => (
          <React.Fragment key={s}>
            <VStack spacing={1}>
              <Flex
                w="32px"
                h="32px"
                borderRadius="full"
                border="2px solid"
                borderColor={step >= s ? "white" : "whiteAlpha.300"}
                bg={step > s ? "white" : "transparent"}
                align="center"
                justify="center"
                transition="all 0.3s"
              >
                <Text
                  color={step > s ? "gray.900" : step === s ? "white" : "whiteAlpha.400"}
                  fontWeight="500"
                  fontSize="xs"
                >
                  {step > s ? "✓" : i + 1}
                </Text>
              </Flex>
              <Text
                color={step >= s ? "white" : "whiteAlpha.400"}
                fontSize="2xs"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight={step === s ? "500" : "400"}
                display={{ base: "none", md: "block" }}
              >
                {stepLabels[i]}
              </Text>
            </VStack>
            {i < 3 && (
              <Box
                flex={1}
                h="2px"
                bg={step > s ? "white" : "whiteAlpha.300"}
                mx={2}
                mt={{ base: 0, md: "-16px" }}
                transition="all 0.3s"
              />
            )}
          </React.Fragment>
        ))}
      </Flex>
    );
  };

  const renderStep1 = () => (
    <VStack spacing={6} align="stretch" py={4}>
      <VStack spacing={2}>
        <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500" textAlign="center">ACCESSO</Text>
        <Text fontSize="lg" textAlign="center" textTransform="uppercase" letterSpacing="wide" fontWeight="400" color="white">COME VUOI PROCEDERE?</Text>
        <Text color="gray.400" textAlign="center" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
          ACCEDI CON GOOGLE PER VELOCIZZARE LA PRENOTAZIONE
        </Text>
      </VStack>

      <VStack spacing={4}>
        <Button
          w="full"
          bg="white"
          color="gray.900"
          borderRadius="none"
          py={6}
          fontWeight="400"
          textTransform="uppercase"
          letterSpacing="wide"
          _hover={{ bg: "gray.200" }}
          leftIcon={<GoogleIcon />}
          onClick={handleGoogleLogin}
        >
          CONTINUA CON GOOGLE
        </Button>

        <HStack w="full" spacing={4}>
          <Divider borderColor="whiteAlpha.300" />
          <Text color="gray.600" fontSize="xs" whiteSpace="nowrap" textTransform="uppercase" letterSpacing="widest">OPPURE</Text>
          <Divider borderColor="whiteAlpha.300" />
        </HStack>

        <Button
          w="full"
          bg="transparent"
          color="white"
          border="1px solid"
          borderColor="whiteAlpha.300"
          borderRadius="none"
          py={6}
          fontWeight="400"
          textTransform="uppercase"
          letterSpacing="wide"
          _hover={{ bg: "whiteAlpha.100" }}
          onClick={handleContinueAsGuest}
        >
          CONTINUA COME OSPITE
        </Button>
      </VStack>

      {user && (
        <Alert status="success" borderRadius="none" bg="whiteAlpha.100" border="1px solid" borderColor="green.500">
          <AlertIcon color="green.400" />
          <Text fontSize="sm" color="white" textTransform="uppercase" letterSpacing="wide">
            CONNESSO COME <strong>{user.userDetails}</strong>
          </Text>
        </Alert>
      )}
    </VStack>
  );

  const inputStyle = {
    bg: "gray.900",
    border: "1px solid",
    borderColor: "gray.700",
    _hover: { borderColor: "gray.600" },
    _focus: { borderColor: "white", boxShadow: "0 0 0 1px white" },
    color: "white",
    borderRadius: "none",
  };

  const labelStyle = {
    color: "gray.400",
    fontSize: "xs",
    textTransform: "uppercase" as const,
    letterSpacing: "wide",
    mb: 2,
  };

  const isStep2Valid = () => {
    if (isAdminEdit) return formData.NumeroPersone > 0;
    const beveragesMatch = formData.NumeroAlcolico + formData.NumeroAnalcolico === formData.NumeroPersone;
    const hasParticipants = formData.NumeroPersone > 0;
    const capacityOk = isWaitingList || formData.NumeroPersone <= postiDisponibili || useWaitingList;
    // All additional participant names must be filled (NumeroPersone - 1)
    const additionalCount = Math.max(0, formData.NumeroPersone - 1);
    const namesOk = additionalCount === 0 || (formData.NomiPartecipanti.length === additionalCount &&
           formData.NomiPartecipanti.every(name => name.trim().length > 0));
    return hasParticipants && beveragesMatch && capacityOk && namesOk;
  };

  const beveragesValid = () => {
    const beveragesMatch = formData.NumeroAlcolico + formData.NumeroAnalcolico === formData.NumeroPersone;
    const hasParticipants = formData.NumeroPersone > 0;
    return hasParticipants && beveragesMatch;
  };

  const renderStep2 = () => {
    const additionalCount = Math.max(0, formData.NumeroPersone - 1);

    return (
    <VStack spacing={5} align="stretch">
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">PARTECIPANTI E BEVANDE</Text>
      
      {editMode && editType === 'listaAttesa' && waitingListPosition && (
        <Alert status="info" borderRadius="none" bg="whiteAlpha.100" border="1px solid" borderColor="blue.500">
          <AlertIcon color="blue.400" />
          <Text fontSize="xs" color="white" textTransform="uppercase" letterSpacing="wide">
            POSIZIONE IN LISTA D'ATTESA: <strong>#{waitingListPosition}</strong>
          </Text>
        </Alert>
      )}
      
      <FormControl>
        <FormLabel {...labelStyle}>NUMERO PARTECIPANTI</FormLabel>
        <Input
          type="number"
          name="NumeroPersone"
          value={formData.NumeroPersone || ""}
          onChange={handleNumeroPersoneChange}
          min={1}
          {...inputStyle}
        />
        {!isWaitingList && postiDisponibili > 0 && (
          <Text fontSize="xs" color="gray.600" mt={2} textTransform="uppercase" letterSpacing="wide">
            {postiDisponibili} POSTI DISPONIBILI SU {evento.PostiTotali}
          </Text>
        )}
      </FormControl>

      {exceedsCapacity && (
        <Alert status="warning" borderRadius="none" bg="whiteAlpha.100" border="1px solid" borderColor="orange.500">
          <AlertIcon color="orange.400" />
          <Box>
            <Text fontWeight="500" textTransform="uppercase" letterSpacing="wide" fontSize="sm">NON CI SONO ABBASTANZA POSTI</Text>
            <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
              RICHIESTI {formData.NumeroPersone}, DISPONIBILI {postiDisponibili}
            </Text>
            <Checkbox
              mt={2}
              isChecked={useWaitingList}
              onChange={(e) => setUseWaitingList(e.target.checked)}
              colorScheme="whiteAlpha"
              sx={{ '.chakra-checkbox__control': { borderColor: 'whiteAlpha.400', borderRadius: 'none' } }}
            >
              <Text fontSize="xs" textTransform="uppercase" letterSpacing="wide" color="gray.400">ISCRIVIMI ALLA LISTA D'ATTESA</Text>
            </Checkbox>
          </Box>
        </Alert>
      )}

      <Box>
        <Text fontSize="xs" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="wide">
          SELEZIONA IL TIPO DI BEVANDA PER OGNI PARTECIPANTE
        </Text>
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <FormControl>
            <FormLabel {...labelStyle}>
              MENU ALCOLICO (€{evento.PrezzoAlcolico})
              {evento.AlcolicoDesc && <Text as="span" color="gray.500" fontWeight="normal" ml={1}>- {evento.AlcolicoDesc}</Text>}
            </FormLabel>
            <Input
              type="number"
              name="NumeroAlcolico"
              value={formData.NumeroAlcolico || ""}
              onChange={handleNumericChange}
              min={0}
              {...inputStyle}
              borderColor={!beveragesValid() ? "red.500" : "whiteAlpha.300"}
              _hover={{ borderColor: !beveragesValid() ? "red.400" : "whiteAlpha.400" }}
            />
          </FormControl>
          <FormControl>
            <FormLabel {...labelStyle}>
              MENU ANALCOLICO (€{evento.PrezzoAnalcolico})
              {evento.AnalcolicoDesc && <Text as="span" color="gray.500" fontWeight="normal" ml={1}>- {evento.AnalcolicoDesc}</Text>}
            </FormLabel>
            <Input
              type="number"
              name="NumeroAnalcolico"
              value={formData.NumeroAnalcolico || ""}
              onChange={handleNumericChange}
              min={0}
              {...inputStyle}
              borderColor={!beveragesValid() ? "red.500" : "whiteAlpha.300"}
              _hover={{ borderColor: !beveragesValid() ? "red.400" : "whiteAlpha.400" }}
            />
          </FormControl>
        </Grid>
        {!beveragesValid() && (
          <Text fontSize="xs" color="red.400" mt={2} textTransform="uppercase" letterSpacing="wide">
            {formData.NumeroAlcolico + formData.NumeroAnalcolico < formData.NumeroPersone
              ? `MANCANO ${formData.NumeroPersone - (formData.NumeroAlcolico + formData.NumeroAnalcolico)} BEVANDE`
              : `${formData.NumeroAlcolico + formData.NumeroAnalcolico - formData.NumeroPersone} BEVANDE IN ECCESSO`}
          </Text>
        )}
      </Box>

      <Box w="full" h="1px" bg="whiteAlpha.200" />

      {/* Gift Card Section */}
      <Box>
        <Text {...labelStyle} mb={3}>GIFT CARD (OPZIONALE)</Text>
        <Text fontSize="xs" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="wide">
          SE HAI UNA GIFT CARD, INSERISCI IL CODICE PER APPLICARLA
        </Text>
        
        {giftCardVerified ? (
          <Box bg="whiteAlpha.100" p={4} border="1px solid" borderColor="green.500">
            <HStack justify="space-between" mb={2}>
              <Text color="green.400" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                ✓ {giftCardVerified.Codice}
              </Text>
              <Button 
                size="xs" 
                bg="transparent" 
                color="red.400" 
                border="1px solid" 
                borderColor="red.400"
                borderRadius="none"
                _hover={{ bg: "red.400", color: "white" }}
                onClick={handleClearGiftCard}
                textTransform="uppercase"
                letterSpacing="wide"
              >
                RIMUOVI
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
              VALIDA PER {giftCardVerified.NumeroPersone} PERSONE 
              ({giftCardVerified.NumeroAlcolico} ALCOLICHE, {giftCardVerified.NumeroAnalcolico} ANALCOLICHE)
            </Text>
          </Box>
        ) : (
          <FormControl isInvalid={!!giftCardError}>
            <HStack>
              <Input
                placeholder="ES. GIFT-XXXX-XXXX"
                value={giftCardCode}
                onChange={(e) => {
                  setGiftCardCode(e.target.value.toUpperCase());
                  setGiftCardError(null);
                }}
                {...inputStyle}
                textTransform="uppercase"
              />
              <Button
                bg="transparent"
                color="white"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="none"
                _hover={{ bg: "whiteAlpha.100" }}
                onClick={handleVerifyGiftCard}
                isLoading={giftCardVerifying}
                isDisabled={!giftCardCode.trim()}
                px={6}
                textTransform="uppercase"
                letterSpacing="wide"
                fontSize="sm"
              >
                VERIFICA
              </Button>
            </HStack>
            {giftCardError && (
              <Text color="red.400" fontSize="xs" mt={2} textTransform="uppercase" letterSpacing="wide">
                {giftCardError}
              </Text>
            )}
          </FormControl>
        )}
      </Box>

      {/* Nomi Partecipanti Section */}
      {additionalCount > 0 && (
        <>
          <Box w="full" h="1px" bg="whiteAlpha.200" />
          
          <Box>
            <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500" mb={3}>NOMI DEGLI ALTRI PARTECIPANTI</Text>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={4}>
              INSERISCI I NOMI DEGLI ALTRI {additionalCount} PARTECIPANT{additionalCount === 1 ? 'E' : 'I'}.
            </Text>

            <VStack spacing={3}>
              {formData.NomiPartecipanti.map((nome, index) => (
                <FormControl key={index} isRequired>
                  <FormLabel {...labelStyle}>PARTECIPANTE {index + 2}</FormLabel>
                  <Input
                    value={nome}
                    onChange={(e) => handleParticipantNameChange(index, e.target.value)}
                    placeholder={`NOME E COGNOME PARTECIPANTE ${index + 2}`}
                    {...inputStyle}
                    borderColor={showStep2Errors && !nome.trim() ? "red.400" : "whiteAlpha.300"}
                    _hover={{ borderColor: showStep2Errors && !nome.trim() ? "red.300" : "whiteAlpha.400" }}
                  />
                </FormControl>
              ))}
            </VStack>
          </Box>
        </>
      )}
    </VStack>
    );
  };

  const renderStep3 = () => (
    <VStack spacing={5} align="stretch" h="100%" flex="1">
      {/* Condizioni */}
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">CONDIZIONI</Text>

      <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
        <VStack align="stretch" spacing={4} fontSize="xs" textTransform="uppercase" letterSpacing="wide">
          <Text color="gray.300">
            CREDIAMO NELL'IMPORTANZA DELLA CONDIVISIONE, PER QUESTO MOTIVO LA CENA SARÀ AD UN <strong>UNICO TAVOLO</strong> INSIEME AGLI ALTRI COMMENSALI.
          </Text>
          <Text color="gray.300">
            IN CASO DI ALLERGIE O INTOLLERANZE <strong>NON È POSSIBILE GARANTIRE</strong> LA NON CONTAMINAZIONE DEGLI ALIMENTI.
          </Text>
          <Text color="gray.300">
            IN CASA VIVONO DUE GATTE, CHE SARANNO COMUNQUE TENUTE IN UN AMBIENTE SEPARATO.
          </Text>
          <Text color="gray.300">
            <strong>NON È POSSIBILE</strong> CHIEDERE VARIAZIONI AL MENÙ.
          </Text>
        </VStack>
      </Box>

      <Box 
        bg="whiteAlpha.50" 
        p={4} 
        border="1px solid" 
        borderColor={showStep3Errors && !termsAccepted ? "red.400" : "whiteAlpha.200"}
        transition="border-color 0.2s"
      >
        <Checkbox
          isChecked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e.target.checked);
            if (e.target.checked) setShowStep3Errors(false);
          }}
          colorScheme="gray"
          borderColor={showStep3Errors && !termsAccepted ? "red.400" : "gray.600"}
        >
          <Text fontSize="xs" textTransform="uppercase" letterSpacing="wide" color={showStep3Errors && !termsAccepted ? "red.300" : "white"}>
            DICHIARO DI AVER LETTO E ACCETTATO LE CONDIZIONI SOPRA RIPORTATE
          </Text>
        </Checkbox>
      </Box>

      <Box w="full" h="1px" bg="whiteAlpha.200" />

      {/* Allergeni */}
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">INGREDIENTI E ALLERGENI</Text>
      
      <Box 
        bg="whiteAlpha.50" 
        p={4} 
        border="1px solid"
        borderColor="whiteAlpha.200"
        flex="1"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: '#1A202C' },
          '&::-webkit-scrollbar-thumb': { background: '#4A5568' },
        }}
      >
        <Text fontSize="xs" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="wide">
          IL MENU CONTIENE I SEGUENTI INGREDIENTI:
        </Text>
        {evento.Ingredienti ? (
          <Box 
            dangerouslySetInnerHTML={{ __html: evento.Ingredienti }}
            color="gray.300"
            fontSize="sm"
            sx={{
              "& p": { margin: 0 },
              "& ul, & ol": { paddingLeft: "1.5em", margin: "0.5em 0" },
              "& li": { marginBottom: "0.25em" },
            }}
          />
        ) : (
          <Text color="gray.500" fontStyle="italic" textTransform="uppercase" letterSpacing="wide" fontSize="xs">
            NESSUN INGREDIENTE SPECIFICATO. CONTATTA L'ORGANIZZATORE.
          </Text>
        )}
      </Box>

      <Box 
        bg="whiteAlpha.50" 
        p={4} 
        border="1px solid" 
        borderColor={showStep3Errors && !allergyConfirmed ? "red.400" : "whiteAlpha.200"} 
        flexShrink={0}
        transition="border-color 0.2s"
      >
        <Checkbox
          isChecked={allergyConfirmed}
          onChange={(e) => {
            setAllergyConfirmed(e.target.checked);
            if (e.target.checked) setShowStep3Errors(false);
          }}
          colorScheme="gray"
          borderColor={showStep3Errors && !allergyConfirmed ? "red.400" : "gray.600"}
        >
          <Text fontSize="xs" color={showStep3Errors && !allergyConfirmed ? "red.300" : "gray.400"} textTransform="uppercase" letterSpacing="wide">
            DICHIARO DI AVER PRESO VISIONE DEGLI INGREDIENTI E CONFERMO CHE 
            NESSUN PARTECIPANTE HA ALLERGIE O INTOLLERANZE
          </Text>
        </Checkbox>
      </Box>
    </VStack>
  );

  const renderStep4 = () => {
    const getInputProps = (fieldName: string, value: string, required: boolean = true) => ({
      ...inputStyle,
      borderColor: showStep4Errors && required && !value.trim() ? "red.400" : "whiteAlpha.300",
      _hover: { borderColor: showStep4Errors && required && !value.trim() ? "red.300" : "whiteAlpha.400" },
    });

    return (
    <VStack spacing={5} align="stretch">
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">DATI DI CONTATTO</Text>

      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>NOME</FormLabel>
          <Input name="Nome" value={formData.Nome} onChange={handleChange} {...getInputProps("Nome", formData.Nome)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>COGNOME</FormLabel>
          <Input name="Cognome" value={formData.Cognome} onChange={handleChange} {...getInputProps("Cognome", formData.Cognome)} />
        </FormControl>
      </Grid>

      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>EMAIL</FormLabel>
          <Input type="email" name="Email" value={formData.Email} onChange={handleChange} {...getInputProps("Email", formData.Email)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>TELEFONO</FormLabel>
          <Input type="tel" name="Telefono" value={formData.Telefono} onChange={handleChange} {...getInputProps("Telefono", formData.Telefono)} />
        </FormControl>
      </Grid>

      <Box w="full" h="1px" bg="whiteAlpha.200" />

      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">FATTURAZIONE</Text>

      <FormControl>
        <FormLabel {...labelStyle}>TIPO SOGGETTO</FormLabel>
        <Select name="TipoSoggetto" value={formData.TipoSoggetto} onChange={handleChange} {...inputStyle}>
          <option value="Privato" style={{ background: '#1A202C' }}>PRIVATO</option>
          <option value="Azienda" style={{ background: '#1A202C' }}>AZIENDA</option>
        </Select>
      </FormControl>

      {formData.TipoSoggetto === "Privato" ? (
        <FormControl isRequired>
          <FormLabel {...labelStyle}>CODICE FISCALE</FormLabel>
          <Input
            name="CodiceFiscale"
            value={formData.CodiceFiscale}
            onChange={handleChange}
            maxLength={16}
            textTransform="uppercase"
            {...getInputProps("CodiceFiscale", formData.CodiceFiscale)}
          />
        </FormControl>
      ) : (
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <FormControl isRequired>
            <FormLabel {...labelStyle}>PARTITA IVA</FormLabel>
            <Input name="PartitaIVA" value={formData.PartitaIVA} onChange={handleChange} maxLength={11} {...getInputProps("PartitaIVA", formData.PartitaIVA || "")} />
          </FormControl>
          <FormControl>
            <FormLabel {...labelStyle}>RAGIONE SOCIALE</FormLabel>
            <Input name="RagioneSociale" value={formData.RagioneSociale} onChange={handleChange} {...inputStyle} />
          </FormControl>
        </Grid>
      )}

      <FormControl isRequired>
        <FormLabel {...labelStyle}>INDIRIZZO</FormLabel>
        <Input name="Indirizzo" value={formData.Indirizzo} onChange={handleChange} {...getInputProps("Indirizzo", formData.Indirizzo)} />
      </FormControl>

      <Grid templateColumns="100px 1fr 80px" gap={4}>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>CAP</FormLabel>
          <Input name="CAP" value={formData.CAP} onChange={handleChange} maxLength={10} {...getInputProps("CAP", formData.CAP)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>CITTÀ</FormLabel>
          <Input name="Citta" value={formData.Citta} onChange={handleChange} {...getInputProps("Citta", formData.Citta)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>PROV.</FormLabel>
          <Input name="Provincia" value={formData.Provincia} onChange={handleChange} maxLength={2} textTransform="uppercase" {...getInputProps("Provincia", formData.Provincia)} />
        </FormControl>
      </Grid>

      <Box w="full" h="1px" bg="whiteAlpha.200" />

      <FormControl>
        <FormLabel {...labelStyle}>NOTE (OPZIONALE)</FormLabel>
        <Textarea
          name="Note"
          value={formData.Note}
          onChange={handleChange}
          placeholder="INSERISCI EVENTUALI NOTE O RICHIESTE PARTICOLARI..."
          {...inputStyle}
          rows={3}
        />
      </FormControl>
    </VStack>
    );
  };

  const renderStep5 = () => (
    <VStack spacing={5} align="stretch">
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">RIEPILOGO</Text>

      <Box bg="whiteAlpha.50" p={5} border="1px solid" borderColor="whiteAlpha.200">
        <Text fontSize="lg" fontWeight="light" letterSpacing="widest" textTransform="uppercase" color="white">{evento.Titolo}</Text>
        <Text color="gray.400" fontSize="xs" mt={2} textTransform="uppercase" letterSpacing="wide">
          {displayDate}
        </Text>
      </Box>

      {(isWaitingList || useWaitingList) && (
        <Alert status="warning" borderRadius="none" bg="whiteAlpha.100" border="1px solid" borderColor="orange.500">
          <AlertIcon color="orange.400" />
          <Text fontSize="xs" color="white" textTransform="uppercase" letterSpacing="wide">
            QUESTA È UN'ISCRIZIONE ALLA <strong>LISTA D'ATTESA</strong>.
          </Text>
        </Alert>
      )}

      <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between">
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">INTESTATARIO</Text>
            <Text fontWeight="medium" color="white" textTransform="uppercase" letterSpacing="wide">{formData.Nome} {formData.Cognome}</Text>
          </HStack>
          <HStack justify="space-between">
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">EMAIL</Text>
            <Text color="white">{formData.Email}</Text>
          </HStack>
          <HStack justify="space-between">
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">TELEFONO</Text>
            <Text color="white">{formData.Telefono}</Text>
          </HStack>
        </VStack>
      </Box>

      <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
        <Text fontWeight="medium" mb={3} textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400">PARTECIPANTI ({formData.NumeroPersone})</Text>
        <VStack align="stretch" spacing={1}>
          <Text fontSize="sm" color="white" textTransform="uppercase" letterSpacing="wide">
            1. {formData.Nome} {formData.Cognome} (INTESTATARIO)
          </Text>
          {formData.NomiPartecipanti.map((nome, index) => (
            <Text key={index} fontSize="sm" color="white" textTransform="uppercase" letterSpacing="wide">
              {index + 2}. {nome}
            </Text>
          ))}
        </VStack>
      </Box>

      <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
        <Text fontWeight="medium" mb={3} textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400">DETTAGLIO PRENOTAZIONE</Text>
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between">
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">PARTECIPANTI</Text>
            <Text color="white">{formData.NumeroPersone}</Text>
          </HStack>
          {formData.NumeroAlcolico > 0 && (
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                ALCOLICHE{evento.AlcolicoDesc && ` (${evento.AlcolicoDesc})`}
              </Text>
              <Text color="white">{formData.NumeroAlcolico} × {formatCurrency(evento.PrezzoAlcolico || 0)} = {formatCurrency(formData.NumeroAlcolico * (evento.PrezzoAlcolico || 0))}</Text>
            </HStack>
          )}
          {formData.NumeroAnalcolico > 0 && (
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                ANALCOLICHE{evento.AnalcolicoDesc && ` (${evento.AnalcolicoDesc})`}
              </Text>
              <Text color="white">{formData.NumeroAnalcolico} × {formatCurrency(evento.PrezzoAnalcolico || 0)} = {formatCurrency(formData.NumeroAnalcolico * (evento.PrezzoAnalcolico || 0))}</Text>
            </HStack>
          )}
          <Box w="full" h="1px" bg="whiteAlpha.200" />
          <HStack justify="space-between">
            <Text fontWeight="medium" textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="white">SUBTOTALE</Text>
            <Text fontWeight="medium" color="white">{formatCurrency(calculateTotal())}</Text>
          </HStack>
          
          {giftCardVerified && (
            <>
              <HStack justify="space-between" bg="whiteAlpha.100" mx={-4} px={4} py={2}>
                <Text color="gray.300" textTransform="uppercase" letterSpacing="wide" fontSize="xs">GIFT CARD ({giftCardVerified.Codice})</Text>
                <Text color="green.400" fontWeight="medium">
                  -{formatCurrency(
                    (giftCardVerified.NumeroAlcolico || 0) * (evento.PrezzoAlcolico || 0) +
                    (giftCardVerified.NumeroAnalcolico || 0) * (evento.PrezzoAnalcolico || 0)
                  )}
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600" mt={-1} textTransform="uppercase" letterSpacing="wide">
                INCLUDE: {giftCardVerified.NumeroAlcolico || 0} ALCOLICHE, {giftCardVerified.NumeroAnalcolico || 0} ANALCOLICHE
              </Text>
            </>
          )}
          
          <Box w="full" h="1px" bg="whiteAlpha.200" />
          <HStack justify="space-between">
            <Text fontWeight="medium" color="white" textTransform="uppercase" letterSpacing="widest" fontSize="sm">DA PAGARE</Text>
            <Text fontWeight="medium" color="white" fontSize="lg">
              {formatCurrency(Math.max(0, 
                calculateTotal() - (
                  giftCardVerified 
                    ? (giftCardVerified.NumeroAlcolico || 0) * (evento.PrezzoAlcolico || 0) +
                      (giftCardVerified.NumeroAnalcolico || 0) * (evento.PrezzoAnalcolico || 0)
                    : 0
                )
              ))}
            </Text>
          </HStack>
        </VStack>
      </Box>

      {formData.Note && (
        <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
          <Text fontWeight="medium" mb={2} textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400">NOTE</Text>
          <Text color="gray.300" fontSize="sm" textTransform="uppercase" letterSpacing="wide">{formData.Note}</Text>
        </Box>
      )}
    </VStack>
  );

  const renderStep6 = () => {
    const showAsWaitingList = isWaitingList || useWaitingList;
    
    // Quiz loading state
    if (quizLoading) {
      return (
        <VStack spacing={6} align="stretch" py={8} justify="center" minH="300px">
          <VStack spacing={4}>
            <Spinner size="xl" color="white" thickness="3px" />
            <Text
              fontSize="sm"
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="wide"
              textAlign="center"
            >
              Sto preparando il quiz...
            </Text>
          </VStack>
        </VStack>
      );
    }

    // Quiz finished - show score
    if (quizFinished) {
      const percentage = Math.round((quizScore / quizQuestions.length) * 100);
      let message = "";
      if (percentage === 100) {
        message = "Perfetto! Sei un vero esperto!";
      } else if (percentage >= 60) {
        message = "Ottimo lavoro!";
      } else {
        message = "Imparerai di più durante la serata!";
      }

      return (
        <VStack spacing={6} align="stretch" py={4}>
          <VStack spacing={4}>
            <Text
              fontSize="xl"
              fontWeight="400"
              color="white"
              textTransform="uppercase"
              letterSpacing="wide"
              textAlign="center"
            >
              QUIZ COMPLETATO
            </Text>
            <Box 
              bg="whiteAlpha.100" 
              p={6} 
              border="1px solid" 
              borderColor="whiteAlpha.200"
              w="full"
            >
              <VStack spacing={2}>
                <Text fontSize="4xl" color="white" fontWeight="bold">
                  {quizScore}/{quizQuestions.length}
                </Text>
                <Text color="gray.400" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                  RISPOSTE CORRETTE
                </Text>
              </VStack>
            </Box>
            <Text
              fontSize="sm"
              color="gray.400"
              textAlign="center"
            >
              {message}
            </Text>
          </VStack>
        </VStack>
      );
    }

    // Quiz active - show current question
    if (quizActive && quizQuestions.length > 0) {
      const currentQuestion = quizQuestions[quizCurrentIndex];
      
      return (
        <VStack spacing={6} align="stretch" py={4}>
          {/* Progress indicator */}
          <HStack justify="space-between" px={2}>
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
              DOMANDA {quizCurrentIndex + 1} DI {quizQuestions.length}
            </Text>
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">
              PUNTEGGIO: {quizScore}
            </Text>
          </HStack>

          {/* Progress bar */}
          <Box w="full" h="2px" bg="whiteAlpha.200">
            <Box 
              h="full" 
              bg="white" 
              w={`${((quizCurrentIndex + 1) / quizQuestions.length) * 100}%`}
              transition="width 0.3s"
            />
          </Box>

          {/* Question */}
          <Box bg="whiteAlpha.50" p={6} border="1px solid" borderColor="whiteAlpha.200">
            <Text color="white" fontSize="lg" textAlign="center" lineHeight="tall">
              {currentQuestion.question}
            </Text>
          </Box>

          {/* Answer options */}
          <VStack spacing={3}>
            {currentQuestion.answers.map((answer, index) => {
              const isSelected = quizSelectedAnswer === index;
              const isCorrect = answer.correct;
              const showResult = quizAnswered;

              let bg = "transparent";
              let borderColor = "whiteAlpha.300";
              let textColor = "white";

              if (showResult) {
                if (isCorrect) {
                  bg = "green.900";
                  borderColor = "green.500";
                  textColor = "green.200";
                } else if (isSelected && !isCorrect) {
                  bg = "red.900";
                  borderColor = "red.500";
                  textColor = "red.200";
                }
              } else if (isSelected) {
                bg = "whiteAlpha.100";
                borderColor = "white";
              }

              return (
                <Box
                  key={index}
                  as="button"
                  w="full"
                  p={4}
                  bg={bg}
                  border="1px solid"
                  borderColor={borderColor}
                  color={textColor}
                  textAlign="left"
                  onClick={() => !quizAnswered && handleQuizAnswer(index)}
                  cursor={quizAnswered ? "default" : "pointer"}
                  _hover={!quizAnswered ? { bg: "whiteAlpha.100", borderColor: "white" } : {}}
                  transition="all 0.2s"
                  disabled={quizAnswered}
                >
                  <HStack justify="space-between">
                    <Text>{answer.text}</Text>
                    {showResult && isCorrect && (
                      <Text color="green.400">✓</Text>
                    )}
                    {showResult && isSelected && !isCorrect && (
                      <Text color="red.400">✗</Text>
                    )}
                  </HStack>
                </Box>
              );
            })}
          </VStack>

          {/* Timer indicator */}
          {!quizAnswered && (
            <Box w="full">
              <Box w="full" h="4px" bg="whiteAlpha.200" overflow="hidden">
                <Box 
                  h="full" 
                  bg={quizTimeLeft <= 2 ? "red.500" : "green.500"}
                  w={`${(quizTimeLeft / 10) * 100}%`}
                  transition="width 1s linear, background-color 0.3s"
                />
              </Box>
              <HStack justify="center" mt={2}>
                <Text 
                  color={quizTimeLeft <= 2 ? "red.400" : "white"} 
                  fontSize="2xl" 
                  fontWeight="bold"
                  fontFamily="mono"
                >
                  {quizTimeLeft}
                </Text>
              </HStack>
            </Box>
          )}

          {/* Timed out message */}
          {quizTimedOut && (
            <Box textAlign="center" py={2}>
              <Text color="red.400" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                TEMPO SCADUTO
              </Text>
            </Box>
          )}
        </VStack>
      );
    }

    // Default: confirmation screen
    return (
      <VStack spacing={6} align="stretch" py={4}>
        {/* Success Icon */}
        <VStack spacing={4}>
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            border="2px solid"
            borderColor="green.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="3xl" color="green.400">✓</Text>
          </Box>
          <Text
            fontSize="xl"
            fontWeight="500"
            color="white"
            textTransform="uppercase"
            letterSpacing="widest"
            textAlign="center"
          >
            {editMode 
              ? "MODIFICHE SALVATE" 
              : (showAsWaitingList ? "ISCRITTO ALLA LISTA D'ATTESA" : "PRENOTAZIONE CONFERMATA")}
          </Text>
          <Text
            fontSize="xs"
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="wide"
            textAlign="center"
          >
            {editMode 
              ? "Le tue modifiche sono state salvate con successo"
              : `Riceverai una email di conferma a ${formData.Email}`}
          </Text>
        </VStack>

        <Box w="full" h="1px" bg="whiteAlpha.200" />

        {/* Riepilogo */}
        <Box bg="whiteAlpha.50" p={5} border="1px solid" borderColor="whiteAlpha.200">
          <Text fontSize="lg" fontWeight="light" letterSpacing="widest" textTransform="uppercase" color="white">{evento.Titolo}</Text>
          <Text color="gray.400" fontSize="xs" mt={2} textTransform="uppercase" letterSpacing="wide">
            {displayDate}
          </Text>
        </Box>

        <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">INTESTATARIO</Text>
              <Text fontWeight="medium" color="white" textTransform="uppercase" letterSpacing="wide">{formData.Nome} {formData.Cognome}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">PARTECIPANTI</Text>
              <Text color="white">{formData.NumeroPersone}</Text>
            </HStack>
            {!showAsWaitingList && (
              <HStack justify="space-between">
                <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">TOTALE</Text>
                <Text fontWeight="medium" color="white">
                  {formatCurrency(Math.max(0, 
                    calculateTotal() - (
                      giftCardVerified 
                        ? (giftCardVerified.NumeroAlcolico || 0) * (evento.PrezzoAlcolico || 0) +
                          (giftCardVerified.NumeroAnalcolico || 0) * (evento.PrezzoAnalcolico || 0)
                        : 0
                    )
                  ))}
                </Text>
              </HStack>
            )}
          </VStack>
        </Box>

        <Box w="full" h="1px" bg="whiteAlpha.200" />

        {isAdminEdit && editType !== 'listaAttesa' && (
          <Checkbox
            isChecked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            isDisabled={!formData.Email.trim()}
            colorScheme="whiteAlpha"
            sx={{ '.chakra-checkbox__control': { borderColor: 'whiteAlpha.400', borderRadius: 'none' } }}
          >
            <Text fontSize="xs" textTransform="uppercase" letterSpacing="wide" color="gray.400">
              INVIA EMAIL DI CONFERMA MODIFICA AL CLIENTE
            </Text>
          </Checkbox>
        )}

        {/* Invito al gioco - only in create mode */}
        {!editMode && (
          <VStack spacing={3} py={2}>
            <Text
              fontSize="xs"
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="wide"
              textAlign="center"
            >
              Mentre aspetti la serata, che ne dici di giocare?
            </Text>
          </VStack>
        )}
      </VStack>
    );
  };  const showAsWaitingList = isWaitingList || useWaitingList;

  const getHeaderLabel = () => {
    if (editMode) {
      return editType === 'listaAttesa' ? "MODIFICA LISTA D'ATTESA" : "MODIFICA PRENOTAZIONE";
    }
    return showAsWaitingList ? "LISTA D'ATTESA" : "PRENOTAZIONE";
  };

  return (
    <Modal isOpen onClose={step === 6 ? handleCloseConfirmation : onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(8px)" />
      <ModalContent bg="gray.900" mx={4} h="85vh" display="flex" flexDirection="column" borderRadius="none" border="1px solid" borderColor="whiteAlpha.200">
        <ModalHeader flexShrink={0} pt={8} pb={4}>
          {step === 6 ? (
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="widest">
              CONFERMA
            </Text>
          ) : (
            <>
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="widest" mb={2}>
                {getHeaderLabel()}
              </Text>
              <Text fontSize="xl" fontWeight="400" color="white" textTransform="uppercase" letterSpacing="wide">
                {evento.Titolo}
              </Text>
            </>
          )}
        </ModalHeader>
        <ModalCloseButton color="gray.400" onClick={step === 6 ? handleCloseConfirmation : onClose} />

        <StepIndicator />

        {error && (
          <Alert status="error" mx={6} mb={4} borderRadius="md" flexShrink={0}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        <ModalBody flex="1" overflowY="auto">
          {authLoading ? (
            <VStack py={8}>
              <Text color="gray.400">Caricamento...</Text>
            </VStack>
          ) : (
            <>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
              {step === 5 && renderStep5()}
              {step === 6 && renderStep6()}
            </>
          )}
        </ModalBody>

        <ModalFooter gap={3} flexShrink={0} borderTop="1px solid" borderColor="whiteAlpha.200">
          {step === 1 && (
            <Button 
              variant="ghost" 
              onClick={onClose}
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="widest"
              fontSize="xs"
              _hover={{ color: "white", bg: "transparent" }}
            >
              ANNULLA
            </Button>
          )}
          {step === 2 && (
            <>
              <Button 
                variant="ghost" 
                onClick={() => user ? onClose() : setStep(1)}
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="widest"
                fontSize="xs"
                _hover={{ color: "white", bg: "transparent" }}
              >
                {user ? "ANNULLA" : "← INDIETRO"}
              </Button>
              <Box
                onClick={() => {
                  if (!isStep2Valid()) {
                    setShowStep2Errors(true);
                  }
                }}
                cursor="pointer"
              >
                <Button 
                  bg="white" 
                  color="black" 
                  onClick={() => goToStep(3)} 
                  isDisabled={!isStep2Valid()}
                  textTransform="uppercase"
                  letterSpacing="widest"
                  fontSize="xs"
                  borderRadius="none"
                  _hover={{ bg: "gray.200" }}
                  _disabled={{ bg: "gray.600", color: "gray.400", pointerEvents: "none" }}
                >
                  AVANTI →
                </Button>
              </Box>
            </>
          )}
          {step === 3 && (
            <>
              <Button 
                variant="ghost" 
                onClick={() => setStep(2)}
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="widest"
                fontSize="xs"
                _hover={{ color: "white", bg: "transparent" }}
              >
                ← INDIETRO
              </Button>
              <Box
                onClick={() => {
                  if (!termsAccepted || !allergyConfirmed) {
                    setShowStep3Errors(true);
                  }
                }}
                cursor="pointer"
              >
                <Button 
                  bg="white" 
                  color="black" 
                  onClick={() => goToStep(4)} 
                  isDisabled={!termsAccepted || !allergyConfirmed}
                  textTransform="uppercase"
                  letterSpacing="widest"
                  fontSize="xs"
                  borderRadius="none"
                  _hover={{ bg: "gray.200" }}
                  _disabled={{ bg: "gray.600", color: "gray.400", pointerEvents: "none" }}
                >
                  AVANTI →
                </Button>
              </Box>
            </>
          )}
          {step === 4 && (
            <>
              <Button 
                variant="ghost" 
                onClick={() => setStep(3)}
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="widest"
                fontSize="xs"
                _hover={{ color: "white", bg: "transparent" }}
              >
                ← INDIETRO
              </Button>
              <Box
                onClick={() => {
                  if (!isStep4Valid()) {
                    setShowStep4Errors(true);
                  }
                }}
                cursor="pointer"
              >
                <Button 
                  bg="white" 
                  color="black" 
                  onClick={() => goToStep(5)} 
                  isDisabled={!isStep4Valid()}
                  textTransform="uppercase"
                  letterSpacing="widest"
                  fontSize="xs"
                  borderRadius="none"
                  _hover={{ bg: "gray.200" }}
                  _disabled={{ bg: "gray.600", color: "gray.400", pointerEvents: "none" }}
                >
                  AVANTI →
                </Button>
              </Box>
            </>
          )}
          {step === 5 && (
            <>
              <Button 
                variant="ghost" 
                onClick={() => setStep(4)}
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="widest"
                fontSize="xs"
                _hover={{ color: "white", bg: "transparent" }}
              >
                ← INDIETRO
              </Button>
              <Button
                bg="white" 
                color="black"
                onClick={handleSubmit}
                isLoading={saving}
                textTransform="uppercase"
                letterSpacing="widest"
                fontSize="xs"
                borderRadius="none"
                _hover={{ bg: "gray.200" }}
              >
                {editMode 
                  ? "SALVA MODIFICHE" 
                  : (showAsWaitingList ? "ISCRIVITI ALLA LISTA D'ATTESA" : "CONFERMA PRENOTAZIONE")}
              </Button>
            </>
          )}
          {step === 6 && (
            <>
              {/* Quiz loading - solo chiudi */}
              {quizLoading && (
                <Button 
                  variant="ghost" 
                  onClick={handleCloseQuiz}
                  color="gray.400"
                  textTransform="uppercase"
                  letterSpacing="widest"
                  fontSize="xs"
                  _hover={{ color: "white", bg: "transparent" }}
                >
                  ANNULLA
                </Button>
              )}
              
              {/* Quiz finished - solo chiudi */}
              {quizFinished && (
                <Button 
                  variant="ghost" 
                  onClick={handleCloseQuiz}
                  color="gray.400"
                  textTransform="uppercase"
                  letterSpacing="widest"
                  fontSize="xs"
                  _hover={{ color: "white", bg: "transparent" }}
                >
                  CHIUDI
                </Button>
              )}
              
              {/* Quiz active - show navigation */}
              {quizActive && !quizLoading && !quizFinished && (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={handleCloseQuiz}
                    color="gray.400"
                    textTransform="uppercase"
                    letterSpacing="widest"
                    fontSize="xs"
                    _hover={{ color: "white", bg: "transparent" }}
                  >
                    ESCI
                  </Button>
                  {quizAnswered && (
                    <Button
                      bg="white" 
                      color="black"
                      onClick={handleNextQuestion}
                      textTransform="uppercase"
                      letterSpacing="widest"
                      fontSize="xs"
                      borderRadius="none"
                      _hover={{ bg: "gray.200" }}
                    >
                      {quizCurrentIndex < quizQuestions.length - 1 ? "PROSSIMA →" : "VEDI RISULTATO →"}
                    </Button>
                  )}
                </>
              )}
              
              {/* Not in quiz - show confirmation buttons */}
              {!quizActive && !quizLoading && !quizFinished && (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={handleCloseConfirmation}
                    color="gray.400"
                    textTransform="uppercase"
                    letterSpacing="widest"
                    fontSize="xs"
                    _hover={{ color: "white", bg: "transparent" }}
                  >
                    CHIUDI
                  </Button>
                  {!editMode && (
                    <Button
                      bg="white" 
                      color="black"
                      onClick={handlePlayGame}
                      textTransform="uppercase"
                      letterSpacing="widest"
                      fontSize="xs"
                      borderRadius="none"
                      _hover={{ bg: "gray.200" }}
                    >
                      GIOCA →
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BookingModal;
